import React, { memo, useCallback, useState } from 'react';
import KyvaIcon from '../../icons/kyva';

interface ClientLogoProps {
  width?: string;
  height?: string;
  logoUrl?: string | null;
  loading?: boolean;
  error?: boolean;
}

const ClientLogo: React.FC<ClientLogoProps> = memo(({ 
  width = "24", 
  height = "24",
  logoUrl = null,
  loading = false,
  error = false
}) => {
  const [imageError, setImageError] = useState(false);

  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error('❌ Client logo image failed to load:', e);
    console.error('Failed URL:', logoUrl);
    setImageError(true);
  }, [logoUrl]);

  const handleImageLoad = useCallback(() => {
    console.log('✅ Client logo loaded successfully from:', logoUrl);
    setImageError(false);
  }, [logoUrl]);

  // Show default KyvaIcon while loading, on error, or when no logo is available
  if (loading || error || !logoUrl || imageError) {
    // Reduce console noise
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Showing KyvaIcon fallback:', { 
        loading, 
        error, 
        hasLogoUrl: !!logoUrl,
        imageError
      });
    }
    return <KyvaIcon width={width} height={height} />;
  }

  // Reduce console noise in production
  if (process.env.NODE_ENV === 'development') {
    console.log('🖼️ Rendering client logo with URL:', logoUrl);
  }
  
  return (
    <img
      src={logoUrl}
      alt="Client Logo"
      width={width}
      height={height}
      style={{
        objectFit: 'contain',
        maxWidth: '100%',
        maxHeight: '100%'
      }}
      onError={handleImageError}
      onLoad={handleImageLoad}
    />
  );
});

ClientLogo.displayName = 'ClientLogo';
export default ClientLogo;